/*
 *  Copyright (C) 2025 - SimpliCity Digital Inc - All Rights Reserved
 */

import React, { useEffect, useState } from "react";
import { SimpliCityAdminSDK } from "@simplicitycms/simplicity-admin-js-sdk";
import { useAuth } from "cms/auth/hooks/use-auth";
import validator from "utils/validator";
import Config from "data/config";
import { objectHasPropertyString } from "utils/empty-exists";
import { ScaledWidthImage } from "cms/images/simplicity-image/scaled-width-image";
import { ScaledHeightImage } from "cms/images/simplicity-image/scaled-height-image";
import { ScaledFullWidthImage } from "cms/images/simplicity-image/scaled-fullwidth-image";
import { CroppedFocusImage } from "cms/images/simplicity-image/cropped-focus-image";
import { IMAGE_TYPES, FORMATS } from "cms/images/image-inpage-editor-v2/image-helpers";
import { Logger } from "cms/utils/log/logger";

const SimplicityImage = (props) => {
    
    const _image = props?.image;
    const settings = props?.settings;
    const options = settings?.options;
    const method = options?.method;
    const useCaption = options?.useCaption;
    // const dimension = options?.dimension;
    // const position = options?.position;
    const enableImageDebug = Config.Debug.useDebug;
    const { token } = useAuth();
    const [type, setType] = useState();
    const [image, setImage] = useState();
    const [hasContent, setHasContent] = useState();
    
    useEffect(() => {
        // component has an image property containing the guid of the image
        const isImage = validator.isImage(_image);
        const guid = isImage ? _image?.guid : _image;
        // First check if the image is improperly saved
        // - we want a guid at this point not an image
        const isGuid = validator.isGuid(guid);
        if (isGuid) {
            const fetchData = async () => {
                const img = await SimpliCityAdminSDK.images.findOne(token, guid);
                setImage(img);
                setHasContent(objectHasPropertyString(img,"guid"));
            };
            fetchData();
        } else {
            Logger.info(`Image property was not valid. [IMAGE: ${JSON.stringify(_image)}]`);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [_image]);
    
    useEffect(() => {
        const _isScaled = settings?.format === FORMATS.SCALED_IMAGE.value;
        if (_isScaled) {
            switch (method) {
                case "WidthPriority":
                    setType(IMAGE_TYPES.SCALED_WIDTH);
                    break;
                case "HeightPriority":
                    setType(IMAGE_TYPES.SCALED_HEIGHT);
                    break;
                case "100Aspect":
                    setType(IMAGE_TYPES.SCALED_ASPECT);
                    break;
                default:
                    setType(IMAGE_TYPES.SCALED_ASPECT);
                    break;
            }
        } else {
            setType(IMAGE_TYPES.CROPPED_FOCUS);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [image, settings, hasContent]);
    
    return (
        <>
            {hasContent &&
                <div style={{ display: "flex", flexDirection: "column" }}>
                    <div>
                        {type === IMAGE_TYPES.SCALED_WIDTH &&
                            <ScaledWidthImage
                                image={image}
                                settings={settings}
                            />
                        }
                        {type === IMAGE_TYPES.SCALED_HEIGHT &&
                            <ScaledHeightImage
                                image={image}
                                settings={settings}
                            />
                        }
                        {type === IMAGE_TYPES.SCALED_ASPECT &&
                            <ScaledFullWidthImage
                                image={image}
                                settings={settings}
                            />
                        }
                        {type === IMAGE_TYPES.CROPPED_FOCUS &&
                            <CroppedFocusImage
                                image={image}
                                settings={settings}
                            />
                        }
                    </div>
                    {useCaption && image?.description &&
                        <div className={"simplicity-image-caption"}>
                            {image?.description}
                        </div>
                    }
                </div>
            }
            {enableImageDebug &&
                <pre className='in-page-debug'>
                    image: {JSON.stringify(image, null, 2)}
                </pre>
            }
        </>
    );
};

export { SimplicityImage };
